import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { db } from "../firebase/config";
import { collection, query, where, orderBy, getDocs, doc, updateDoc } from "firebase/firestore";
import "./Orders.css";

var BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:4000";

var STATUS_MAP = {
  confirmed: { bg: "#EAF5E0", color: "#2D5016", label: "Confirmed", step: 1 },
  processing: { bg: "#FFF8EC", color: "#C8860A", label: "Processing", step: 2 },
  shipped: { bg: "#E8F0FE", color: "#1A56DB", label: "Shipped", step: 3 },
  delivered: { bg: "#EAF5E0", color: "#2D5016", label: "Delivered", step: 4 },
  cancelled: { bg: "#FDE8E8", color: "#8B0000", label: "Cancelled", step: 0 },
};

var TIMELINE_STEPS = ["Confirmed", "Processing", "Shipped", "Delivered"];

var PRODUCT_IMAGES = {
  "hara-namak": "/static/media/jar-hara.4d204457cebfb78a8ad8.webp",
  turmeric: "/static/media/jar-turmeric.f21f160879872c96e53d.webp",
  honey: "/static/media/jar-honey.41fd15e8468908e77891.webp",
  "hemp-salt": "/static/media/jar-hara.4d204457cebfb78a8ad8.webp",
  "flax-salt": "/static/media/jar-hara.4d204457cebfb78a8ad8.webp",
  "test-1": "/static/media/jar-turmeric.f21f160879872c96e53d.webp",
};

function getProductImage(item) {
  var id = (item.id || item.name || "").toLowerCase().replace(/ /g, "-");
  var keys = Object.keys(PRODUCT_IMAGES);
  for (var i = 0; i < keys.length; i++) {
    if (id.indexOf(keys[i]) !== -1) return PRODUCT_IMAGES[keys[i]];
  }
  return PRODUCT_IMAGES["hara-namak"];
}

export default function Orders() {
  var { currentUser } = useAuth();
  var { addToCart } = useCart();
  var [orders, setOrders] = useState([]);
  var [loading, setLoading] = useState(true);
  var [cancellingId, setCancellingId] = useState(null);
  var [cancelReason, setCancelReason] = useState("");
  var [showCancelModal, setShowCancelModal] = useState(null);
  var [expandedAddress, setExpandedAddress] = useState(null);
  var [reorderMsg, setReorderMsg] = useState(null);

  useEffect(function() {
    async function fetchOrders() {
      if (!currentUser) return;
      try {
        var q = query(
          collection(db, "orders"),
          where("userId", "==", currentUser.uid),
          orderBy("createdAt", "desc")
        );
        var snapshot = await getDocs(q);
        var data = snapshot.docs.map(function(d) { return { id: d.id, ...d.data() }; });
        setOrders(data);
      } catch (err) {
        console.error("Error fetching orders:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchOrders();
  }, [currentUser]);

  var handleCancel = async function(order) {
    if (!cancelReason.trim()) return;
    setCancellingId(order.id);
    try {
      await updateDoc(doc(db, "orders", order.id), {
        status: "cancelled",
        cancelReason: cancelReason.trim(),
        cancelledAt: new Date().toISOString(),
      });
      await updateDoc(doc(db, "users", currentUser.uid, "orders", order.id), {
        status: "cancelled",
      });
      fetch(BACKEND_URL + "/api/emails/cancelled", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          customerEmail: order.customerEmail || currentUser.email,
          customerName: order.customerName || currentUser.displayName,
          orderId: order.orderId || order.id,
          amount: order.amount,
        }),
      }).catch(function(e) { console.error("Cancel email failed:", e); });
      setOrders(function(prev) { return prev.map(function(o) { return o.id === order.id ? { ...o, status: "cancelled" } : o; }); });
      setShowCancelModal(null);
      setCancelReason("");
    } catch (err) {
      console.error("Cancel failed:", err);
    } finally {
      setCancellingId(null);
    }
  };

  var canCancel = function(order) {
    return ["confirmed", "processing"].indexOf(order.status) !== -1;
  };

  var getTrackingUrl = function(order) {
    if (order.trackingUrl) return order.trackingUrl;
    if (order.awb) return "https://shiprocket.co/tracking/" + order.awb;
    return null;
  };

  var downloadInvoice = function(order) {
    var items = (order.products || []).map(function(p) {
      return { id: p.id, name: p.name, qty: p.quantity, price: p.price };
    });
    var addr = order.address || {};
    var params = new URLSearchParams({
      customerName: order.customerName || "",
      customerEmail: order.customerEmail || "",
      customerPhone: addr.phone || order.customerPhone || "",
      state: addr.state || "",
      line1: addr.line1 || "",
      line2: addr.line2 || "",
      city: addr.city || "",
      pincode: addr.pincode || "",
      amount: order.amount || "",
      paymentId: order.razorpayPaymentId || "",
      items: JSON.stringify(items),
    });
    window.open(BACKEND_URL + "/api/invoice/" + (order.orderId || order.id) + "?" + params.toString(), "_blank");
  };

  var handleReorder = function(order) {
    (order.products || []).forEach(function(item) {
      if (item.id && item.id !== "test-1") {
        addToCart(item.id);
      }
    });
    setReorderMsg(order.id);
    setTimeout(function() { setReorderMsg(null); }, 3000);
  };

  var getNeedHelpUrl = function(order) {
    var subject = encodeURIComponent("Help with order " + (order.orderId || order.id));
    var body = encodeURIComponent("Hi PureJar team,\n\nI need help with my order:\n\nOrder ID: " + (order.orderId || order.id) + "\nAmount: Rs." + order.amount + "\n\nMy issue:\n\n");
    return "mailto:wecare@purejar.in?subject=" + subject + "&body=" + body;
  };

  if (loading) return (
    <div className="orders-page">
      <div className="page-container"><div className="spinner"></div></div>
    </div>
  );

  return (
    <div className="orders-page">
      <div className="page-container">
        <span className="section-label">My Account</span>
        <h1 className="orders-title">My Orders</h1>

        {orders.length === 0 ? (
          <div className="orders-empty">
            <span role="img" aria-label="package">&#128230;</span>
            <h2>No orders yet</h2>
            <p>Your orders will appear here once you make a purchase.</p>
            <Link to="/products" className="btn-primary">Shop Now</Link>
          </div>
        ) : (
          <div className="orders-list">
            {orders.map(function(order) {
              var status = STATUS_MAP[order.status] || STATUS_MAP.confirmed;
              var trackUrl = getTrackingUrl(order);
              var addr = order.address || {};
              var currentStep = status.step || 0;
              return (
                <div key={order.id} className="order-card card">
                  {/* Header */}
                  <div className="order-header">
                    <div>
                      <p className="order-id-label">Order ID</p>
                      <strong className="order-id">{order.orderId || order.id}</strong>
                      <p className="order-date-inline">
                        {order.createdAt && order.createdAt.toDate ? order.createdAt.toDate().toLocaleDateString("en-IN", {
                          day: "numeric", month: "short", year: "numeric"
                        }) : "\u2014"}
                      </p>
                    </div>
                    <span className="order-status" style={{ background: status.bg, color: status.color }}>
                      {status.label}
                    </span>
                  </div>

                  {/* Timeline - only for non-cancelled orders */}
                  {order.status !== "cancelled" && (
                    <div className="order-timeline">
                      {TIMELINE_STEPS.map(function(step, idx) {
                        var stepNum = idx + 1;
                        var isActive = stepNum <= currentStep;
                        var isCurrent = stepNum === currentStep;
                        return (
                          <div key={step} className={"timeline-step" + (isActive ? " active" : "") + (isCurrent ? " current" : "")}>
                            <div className="timeline-dot"></div>
                            {idx < 3 && <div className="timeline-line"></div>}
                            <span className="timeline-label">{step}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Products */}
                  <div className="order-products">
                    {(order.products || []).map(function(item, idx) {
                      return (
                        <div key={idx} className="order-product">
                          <img src={getProductImage(item)} alt={item.name} className="order-product-img" />
                          <div className="order-product-info">
                            <strong>{item.name}</strong>
                            <p>{"Qty: " + item.quantity + " \u00d7 \u20b9" + item.price}</p>
                          </div>
                          <strong className="order-product-price">{"\u20b9" + (item.price * item.quantity)}</strong>
                        </div>
                      );
                    })}
                  </div>

                  {/* Shipping info */}
                  {order.awb && (
                    <div className="order-shipping-info">
                      <span>{"\ud83d\udce6 " + (order.courier || "Courier")}</span>
                      <span>{"AWB: " + order.awb}</span>
                    </div>
                  )}

                  {/* Delivery address - expandable */}
                  {addr.line1 && (
                    <div className="order-address-section">
                      <button className="order-address-toggle" onClick={function() { setExpandedAddress(expandedAddress === order.id ? null : order.id); }}>
                        {expandedAddress === order.id ? "\u25B2 Hide delivery address" : "\u25BC Show delivery address"}
                      </button>
                      {expandedAddress === order.id && (
                        <div className="order-address-detail">
                          <p className="addr-name">{addr.name || order.customerName || ""}</p>
                          <p>{addr.line1}{addr.line2 ? ", " + addr.line2 : ""}</p>
                          <p>{addr.city + ", " + addr.state + " \u2014 " + addr.pincode}</p>
                          {addr.phone && <p>{"\ud83d\udcf1 " + addr.phone}</p>}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Payment info */}
                  {order.razorpayPaymentId && (
                    <div className="order-payment-info">
                      <span>Paid via Razorpay</span>
                      <span className="payment-id">{"ID: " + order.razorpayPaymentId}</span>
                    </div>
                  )}

                  {/* Footer: Total + Actions */}
                  <div className="order-footer">
                    <div className="order-total">
                      {"Total: "}<strong>{"\u20b9" + order.amount}</strong>
                    </div>
                    <div className="order-actions">
                      {trackUrl && (
                        <a href={trackUrl} target="_blank" rel="noopener noreferrer" className="order-btn order-btn-track">
                          Track Order
                        </a>
                      )}
                      {order.status !== "cancelled" && (
                        <button className="order-btn order-btn-invoice" onClick={function() { downloadInvoice(order); }}>
                          Invoice
                        </button>
                      )}
                      <button className="order-btn order-btn-reorder" onClick={function() { handleReorder(order); }}>
                        {reorderMsg === order.id ? "\u2713 Added to cart!" : "Buy Again"}
                      </button>
                      {canCancel(order) && (
                        <button className="order-btn order-btn-cancel" onClick={function() { setShowCancelModal(order.id); setCancelReason(""); }}>
                          Cancel
                        </button>
                      )}
                      {order.status === "cancelled" && (
                        <span className="order-cancelled-note">Refund in 5&#8211;7 days</span>
                      )}
                    </div>
                  </div>

                  {/* Need help */}
                  <div className="order-help">
                    <a href={getNeedHelpUrl(order)} className="order-help-link">Need help with this order?</a>
                  </div>

                  {/* Cancel Modal */}
                  {showCancelModal === order.id && (
                    <div className="cancel-modal">
                      <div className="cancel-modal-content">
                        <h3>Why do you want to cancel?</h3>
                        <p>{"We\u2019d love to know so we can improve."}</p>
                        <select value={cancelReason} onChange={function(e) { setCancelReason(e.target.value); }} className="cancel-reason-select">
                          <option value="">Select a reason...</option>
                          <option value="Changed my mind">Changed my mind</option>
                          <option value="Ordered by mistake">Ordered by mistake</option>
                          <option value="Found a better price">Found a better price</option>
                          <option value="Delivery taking too long">Delivery taking too long</option>
                          <option value="Want to change address">Want to change address</option>
                          <option value="Other">Other</option>
                        </select>
                        <div className="cancel-modal-actions">
                          <button className="order-btn order-btn-cancel-confirm" onClick={function() { handleCancel(order); }} disabled={!cancelReason || cancellingId === order.id}>
                            {cancellingId === order.id ? "Cancelling..." : "Confirm Cancellation"}
                          </button>
                          <button className="order-btn order-btn-keep" onClick={function() { setShowCancelModal(null); }}>
                            Keep My Order
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
